import React, { Suspense, useContext } from 'react';
import { Navigate, Route, Routes, useMatch } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, Grid, Stack } from '@mui/material';

import { useRouter } from '@cvt/routing';
import { BodyLoading } from '@cvt/components/layout/BodyLoading';

import WizardStepIndicator from '@modules/Tributes/components/WizardStepIndicator';
import { TributeWizardContext } from '@modules/Tributes/contexts/TributeWizardContext';

import { OrderTributeCard } from '@pages/Tributes/OrderTributeCard';
import { Authenticated, NotAuthenticated } from '@modules/Auth/components/Authenticated';
import { DetailsStepWrapper } from '../Wizard/DetailsStepWrapper';
import { MediaUploadStep } from '../Wizard/MediaUploadStep';
import { PreviewStep } from '../Wizard/PreviewStep';

import { Hero } from './Hero';

export type TributeFormData = {
  petName: string;
  petType: string;
  sex: string;
  petDescription: string;
  petDateOfBirth: Date | null;
  petDateOfPassing: Date | null;
  favouriteMemory: string;
  personality: string;
  specialHabits: string;
  mediaFiles: File[];
  mainPhoto: File | null;
  firstName: string;
  email: string;
  phone: string;
};

export const CreateTribute: React.FC = () => {
  const router = useRouter();
  const { data: cachedDetails, clearData: clearCachedDetails } = useContext(TributeWizardContext);
  const methods = useForm<TributeFormData>({
    defaultValues: {
      petName: cachedDetails?.details?.petName || '',
      petType: cachedDetails?.details?.petType || '',
      sex: cachedDetails?.details?.sex || '',
      petDescription: '',
      petDateOfBirth: cachedDetails?.details?.petDateOfBirth || null,
      petDateOfPassing: cachedDetails?.details?.petDateOfPassing || null,
      favouriteMemory: '',
      personality: '',
      specialHabits: '',
      mediaFiles: [],
      mainPhoto: null,
      firstName: '',
      email: '',
      phone: '',
    },
  });

  const onPetDetailsComplete = React.useCallback(() => {
    router.tributes.create.media.go();
  }, [router]);

  const onPetMediaStepComplete = React.useCallback(() => {
    clearCachedDetails();
    router.tributes.create.story.go();
  }, [clearCachedDetails, router]);

  const onPreviewStepComplete = React.useCallback(() => {
    router.tributes.create.preview.go();
  }, [router]);

  const isPetDetailsPage = !!useMatch('/tributes/create/details/*');

  return (
    <FormProvider {...methods}>
      <Box
        sx={{
          backgroundImage: 'linear-gradient(to right bottom, rgb(254, 247, 246), rgb(249, 249, 250))',
        }}
        paddingX={{ xs: 2, sm: 4, md: 6, lg: 8, xl: 10 }}
        pb={10}
      >
        <Grid container maxWidth="lg" width="100%" height="100%" mx="auto">
          <Grid size={12}>
            <Stack justifyContent="space-between" width="100%" mt={6}>
              <Hero cardsOnly={!isPetDetailsPage} />
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                sx={{ p: 2 }}
              >
                <WizardStepIndicator/>
              </Stack>

              <NotAuthenticated>
                <Suspense fallback={<BodyLoading height="100%" />}>
                  <DetailsStepWrapper onComplete={onPetDetailsComplete} />
                </Suspense>
              </NotAuthenticated>
              <Authenticated>
                <Routes>
                  <Route path="details" element={(
                    <Suspense fallback={<BodyLoading height="100%" />}>
                      <DetailsStepWrapper onComplete={onPetDetailsComplete} />
                    </Suspense>
                  )}/>
                  <Route path="media" element={(
                    <Suspense fallback={<BodyLoading height="100%" />}>
                      <MediaUploadStep
                        onComplete={onPetMediaStepComplete}
                      />
                    </Suspense>
                  )}/>

                  <Route path="story" element={(
                    <Suspense fallback={<BodyLoading height="100%" />}>
                      <PreviewStep
                        onComplete={onPreviewStepComplete}
                        initialData={cachedDetails?.preview ? cachedDetails.preview : undefined}
                        isCreating={true}
                      />
                    </Suspense>
                  )}/>
                  <Route
                    path="order/:tributeId"
                    element={(
                      <Suspense fallback={<BodyLoading height="100%" />}>
                        <OrderTributeCard />
                      </Suspense>
                    )}
                  />
                  <Route path="*" element={<Navigate to={router.tributes.create.details.path} replace/>}/>
                </Routes>
              </Authenticated>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  );
};
